const URL_PATTERN = /^https?:\/\//i;
const VIDEO_EXTENSIONS = /\.(mp4|webm|mov)(\?|#|$)/i;

export const isMediaUrl = (value: string): boolean =>
  URL_PATTERN.test(value.trim());

export const isVideoUrl = (url: string): boolean =>
  VIDEO_EXTENSIONS.test(url.toLowerCase());

type MediaDisplayItem =
  | { kind: "url"; value: string }
  | { kind: "text"; value: string };

export const extractMediaDisplayItems = (
  value: unknown
): MediaDisplayItem[] => {
  if (value === null || value === undefined) {
    return [];
  }

  if (typeof value === "string") {
    return [{ kind: "text", value }];
  }

  if (Array.isArray(value)) {
    return value.flatMap(item => extractMediaDisplayItems(item));
  }

  if (typeof value === "object") {
    const record = value as Record<string, unknown>;

    if (typeof record.url === "string") {
      return [{ kind: "text", value: record.url }];
    }
  }

  return [{ kind: "text", value: String(value) }];
};

export const formatMediaDisplayValue = (value: unknown): string | null => {
  const items = extractMediaDisplayItems(value);

  if (items.length === 0) {
    return null;
  }

  return items.map(item => item.value).join(", ");
};

const PREVIEW_BOX_CLASS =
  "relative h-[80px] w-[80px] shrink-0 overflow-hidden rounded-md";

const MediaPreview = ({ url }: { url: string }) => {
  if (isVideoUrl(url)) {
    return (
      <video
        src={url}
        controls
        className={`${PREVIEW_BOX_CLASS} object-cover`}
      />
    );
  }

  return (
    <img
      src={url}
      alt="Media preview"
      className={`${PREVIEW_BOX_CLASS} object-cover`}
    />
  );
};

export const MediaPreviewValue = ({ value }: { value: unknown }) => {
  const items = extractMediaDisplayItems(value).map(item =>
    item.kind === "text" && isMediaUrl(item.value)
      ? { kind: "url" as const, value: item.value.trim() }
      : item
  );

  if (items.length === 0) {
    return null;
  }

  if (!items.some(item => item.kind === "url")) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {items.map((item, index) =>
        item.kind === "url" ? (
          <MediaPreview key={`${item.value}-${index}`} url={item.value} />
        ) : (
          <span key={`${item.value}-${index}`} className="whitespace-pre-wrap">
            {item.value}
          </span>
        )
      )}
    </div>
  );
};
